/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { LegacyEquity, ProductType, SynthOIEquity } from '../../../types';
import usePrices from '../../../hooks/equityhub/usePrices';
import { InfoButton, UpsellModal, WatchlistMultiSelect } from '../../shared';
import {
  pricesState,
  selectedEquitySymbolState,
  synthesizedEquitiesState,
  unpricedEquitiesState,
} from '../../../states/equityhub';
import { watchlistsState } from '../../../states';
import {
  getColumnsForScanner,
  getScannerData,
  hasKeyEquityFields,
  sanitizeFilterModel,
} from '../../../util';
import {
  EquityTableType,
  useEquityGridColumns,
} from './useEquityScannerGridColumns';
import {
  GridCallbackDetails,
  GridRowParams,
  MuiEvent,
} from '@spotgamma/x-data-grid-premium';
import { useSetSym } from '../../../hooks';
import {
  defaultColumnScannerVisibility,
  scannerFields,
  EQUITYHUB_UPSELL,
} from '../../../config';
import useAuth from '../../../hooks/auth/useAuth';
import { EquityScannerGrid } from '../../stock_scanner/EquityScannerGrid';
import PanelFullscreenButton from 'components/shared/PanelFullscreenButton';
import PanelFoldButton from 'components/shared/PanelFoldButton';
import ScannersSection from 'components/stock_scanner/ScannersSection';
import useSynthOi from 'hooks/equityhub/useSynthOi';
import { useScannerUrlState } from 'hooks/scanners/useScannerUrlState';
import { useScannerGridState } from 'hooks/scanners/useScannerGridState';
import { usePersistedState } from 'hooks/usePersistedState';

export const EquityhubStockScanner = () => {
  const unpricedEquities = useRecoilValue(unpricedEquitiesState);
  const equities = useRecoilValue(synthesizedEquitiesState);
  const watchlists = useRecoilValue(watchlistsState);
  const setPrices = useSetRecoilState(pricesState);
  const [dpiRange, setDPIRange] = useState<number[] | undefined>(undefined);
  const { hasAccessToProduct } = useAuth();
  const hasEquityhubAccess = hasAccessToProduct(ProductType.EQUITYHUB);
  const [selectedEquitySymbol, setSelectedSymbol] = useRecoilState(
    selectedEquitySymbolState,
  );
  const [upsellOpen, setUpsellOpen] = useState<boolean>(false);

  const { setSym } = useSetSym();

  // Scanner selection via URL (namespaced with 'eh' prefix)
  const { activeScanner, setActiveScanner } = useScannerUrlState('eh');

  // Grid internals via useState
  const gridState = useScannerGridState();

  const { getPrices } = usePrices();
  const theme = useTheme();

  const { isSynthOI } = useSynthOi();

  const { columns: gridCols, columnGroups: gridColGroups } =
    useEquityGridColumns(
      theme,
      dpiRange,
      EquityTableType.StockScreener,
      isSynthOI,
      !hasEquityhubAccess
        ? [...equities.values()]
            .filter((e) => !hasKeyEquityFields(e))
            .map((e) => e.sym)
        : undefined,
    );

  useEffect(() => {
    if (isSynthOI) {
      return;
    }
    const dpiArray = [...equities.values()].map((d) => d.dpi);
    setDPIRange([Math.min(...dpiArray), Math.max(...dpiArray)]);
  }, [equities, isSynthOI, setDPIRange]);

  useEffect(() => {
    if (gridState.useDefaultColumns) {
      gridState.setColumnVisibilityModel(
        activeScanner
          ? getColumnsForScanner(activeScanner)
          : defaultColumnScannerVisibility,
      );
    }
  }, [gridState.useDefaultColumns, activeScanner]);

  const scannerData = useMemo(
    () =>
      getScannerData({
        equities,
        watchlists,
        activeWatchlistIds: gridState.activeWatchlistIds,
        activeScanner,
        hasAccess: hasEquityhubAccess,
      }),
    [
      activeScanner,
      gridState.activeWatchlistIds,
      watchlists,
      equities,
      hasEquityhubAccess,
    ],
  );

  useEffect(() => {
    async function fetchVisiblePriceData() {
      const newPrices: Map<string, number> = await getPrices([]);
      setPrices((prices) => new Map([...prices, ...newPrices]));
    }
    fetchVisiblePriceData();
  }, [getPrices, setPrices, unpricedEquities]);

  // Persist all scanner state to localStorage
  const persistedState = useMemo(
    () => ({
      scanner: activeScanner,
      ...gridState,
    }),
    [activeScanner, gridState],
  );

  const restoreState = useCallback(
    (saved: typeof persistedState) => {
      if (saved.scanner !== undefined) {
        setActiveScanner(saved.scanner);
      }
      if (saved.activeWatchlistIds) {
        gridState.setActiveWatchlistIds(saved.activeWatchlistIds);
      }
      if (saved.sortModel) {
        gridState.setSortModel(saved.sortModel);
      }
      if (saved.columnVisibilityModel) {
        gridState.setColumnVisibilityModel(saved.columnVisibilityModel);
      }
      if (saved.pinnedColumns) {
        gridState.setPinnedColumns(saved.pinnedColumns);
      }
      if (saved.filterModel !== undefined) {
        gridState.setFilterModel(
          sanitizeFilterModel(saved.filterModel, gridCols),
        );
      }
      if (saved.columnOrder) {
        gridState.setColumnOrder(saved.columnOrder);
      }
      if (saved.useDefaultColumns !== undefined) {
        gridState.setUseDefaultColumns(saved.useDefaultColumns);
      }
    },
    [setActiveScanner, gridState, gridCols],
  );

  usePersistedState('eh_scanner_grid_state', persistedState, restoreState, [
    'eh_scanner',
  ]);

  const setSelectedRow = (row: LegacyEquity | SynthOIEquity) => {
    const newSym = row.sym;
    setSelectedSymbol(newSym);
    setSym(newSym, ProductType.EQUITYHUB);
  };

  const isRowDisabled = (row: LegacyEquity | SynthOIEquity) => {
    return !hasEquityhubAccess && !row.live;
  };

  const onRowClick = (
    params: GridRowParams,
    _event: MuiEvent,
    _details: GridCallbackDetails,
  ) => {
    const row = params.row as LegacyEquity | SynthOIEquity;
    if (isRowDisabled(row)) {
      setUpsellOpen(true);
    } else {
      setSelectedRow(row);
    }
  };

  return (
    <Box
      sx={{
        margin: 'auto',
        padding: 3,
        height: '100%',
        maxHeight: '100vh',
      }}
    >
      <EquityScannerGrid
        currentSym={selectedEquitySymbol}
        columns={gridCols}
        data={scannerData}
        columnGroups={gridColGroups}
        onRowClick={onRowClick}
        activeScanner={activeScanner}
        activeWatchlistIds={gridState.activeWatchlistIds}
        useDefaultColumns={gridState.useDefaultColumns}
        setUseDefaultColumns={gridState.setUseDefaultColumns}
        columnVisibilityModel={gridState.columnVisibilityModel}
        setColumnVisibilityModel={gridState.setColumnVisibilityModel}
        pinnedColumns={gridState.pinnedColumns}
        setPinnedColumns={gridState.setPinnedColumns}
        sortModel={gridState.sortModel}
        setSortModel={gridState.setSortModel}
        filterModel={gridState.filterModel}
        setFilterModel={gridState.setFilterModel}
        columnOrder={gridState.columnOrder}
        setColumnOrder={gridState.setColumnOrder}
        defaultColumnOrder={scannerFields}
        customSettings={
          <Box>
            <InfoButton key="eh-levels-info" articleKey="eh-levels-info" />
            <WatchlistMultiSelect
              activeWatchlistIds={gridState.activeWatchlistIds}
              setActiveWatchlistIds={gridState.setActiveWatchlistIds}
            />
            <PanelFullscreenButton panelType="bottom" />
            <PanelFoldButton panelType="bottom" />
          </Box>
        }
        customScannerSettings={
          <ScannersSection
            activeScanner={activeScanner}
            setActiveScanner={setActiveScanner}
          />
        }
        showScannerInfo
      />
      <UpsellModal
        open={upsellOpen}
        setOpen={setUpsellOpen}
        title={EQUITYHUB_UPSELL.title}
        subtitle={EQUITYHUB_UPSELL.subtitle}
        items={EQUITYHUB_UPSELL.items}
      />
    </Box>
  );
};
